import React, { useState } from 'react';
import {
  useGetSessionsQuery,
  useRestoreSessionMutation,
  useForceDeleteSessionMutation,
} from '@/store/api/sessionsApi';
import { Loader2, Search, ChevronLeft, ChevronRight, RotateCcw, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useDebounce } from '@/hooks/useDebounce';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { useNotifications } from '@/hooks/useNotifications';
import { Loader } from '@/components/common/Loader';
import { ConfirmationModal } from '@/components/common/ConfirmationModal';

export function TrashPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [page, setPage] = useState(1);
  const take = 10;
  const skip = (page - 1) * take;

  const {
    data: response,
    isLoading,
    error,
    isFetching,
  } = useGetSessionsQuery({
    search: debouncedSearch || undefined,
    skip,
    take,
    isDeleted: true,
  });

  const [restoreSession, { isLoading: isRestoring }] = useRestoreSessionMutation();
  const [forceDeleteSession, { isLoading: isDeleting }] = useForceDeleteSessionMutation();

  const [sessionToRestore, setSessionToRestore] = useState<{ id: number; title: string } | null>(
    null
  );
  const [sessionToDelete, setSessionToDelete] = useState<{ id: number; title: string } | null>(
    null
  );

  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const sessions = response?.data || [];
  const totalPages = response ? Math.ceil(response.count / take) : 0;

  const { success: notifySuccess, error: notifyError } = useNotifications();

  const confirmRestore = async () => {
    if (!sessionToRestore) return;
    try {
      await restoreSession(sessionToRestore.id).unwrap();
      notifySuccess('Session restored successfully.');
      setSessionToRestore(null);
    } catch (err: any) {
      notifyError(err?.data?.message || 'Failed to restore session. Please try again.');
    }
  };

  const confirmDelete = async () => {
    if (!sessionToDelete) return;
    try {
      await forceDeleteSession(sessionToDelete.id).unwrap();
      notifySuccess('Session permanently deleted.');
      setSessionToDelete(null);
    } catch (err: any) {
      notifyError(err?.data?.message || 'Failed to permanently delete session. Please try again.');
    }
  };

  if (isLoading && !sessions.length) {
    return <Loader />;
  }

  if (error) {
    return (
      <ErrorBanner
        type="error"
        title="Failed to load trash"
        message="There was an error loading the trashed sessions. Please try again."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Trash</h1>
        <p className="text-muted-foreground">
          View or permanently delete sessions that were moved to the trash.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 pb-4">
          <div>
            <CardTitle>Deleted Sessions</CardTitle>
            <CardDescription>{response?.count ?? 0} sessions in trash</CardDescription>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search title, speaker..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {isFetching && (
              <div className="absolute inset-0 bg-background/50 z-10 flex items-center justify-center rounded-lg">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}

            {sessions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b">
                    <tr>
                      <th className="px-4 py-3 font-medium">Title</th>
                      <th className="px-4 py-3 font-medium">Speaker</th>
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sessions.map((session) => (
                      <tr key={session.id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-4 font-medium text-foreground max-w-xs">
                          <div className="truncate" title={session.title}>
                            {session.title}
                          </div>
                          <div className="text-xs text-muted-foreground font-normal">
                            {session.slug}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-muted-foreground">{session.speaker}</td>
                        <td className="px-4 py-4 text-muted-foreground whitespace-nowrap">
                          {session.date}
                        </td>
                        <td className="px-4 py-4">
                          <Badge variant="outline" className="text-muted-foreground border-dashed">
                            Deleted
                          </Badge>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 gap-1 text-primary hover:text-primary hover:bg-primary/10"
                              onClick={() =>
                                setSessionToRestore({ id: session.id, title: session.title })
                              }
                            >
                              <RotateCcw className="h-4 w-4" /> Restore
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() =>
                                setSessionToDelete({ id: session.id, title: session.title })
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Trash2 className="mx-auto h-8 w-8 mb-3 opacity-20" />
                <p className="font-medium mb-1">Trash is empty</p>
                <p className="text-sm">
                  {searchTerm
                    ? 'No deleted sessions match your search.'
                    : 'No sessions have been deleted.'}
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {response && response.count > 0 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Showing <span className="font-medium">{skip + 1}</span> to{' '}
                <span className="font-medium">{Math.min(skip + take, response.count)}</span> of{' '}
                <span className="font-medium">{response.count}</span> results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                <div className="text-sm font-medium">
                  Page {page} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmationModal
        isOpen={!!sessionToRestore}
        onClose={() => setSessionToRestore(null)}
        onConfirm={confirmRestore}
        isLoading={isRestoring}
        title="Restore Session"
        description={`Are you sure you want to restore "${sessionToRestore?.title}"? It will be moved back to the main list as a Draft.`}
        confirmText="Restore"
        isDestructive={false}
      />

      <ConfirmationModal
        isOpen={!!sessionToDelete}
        onClose={() => setSessionToDelete(null)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Permanently Delete"
        description={`Are you sure you want to permanently delete "${sessionToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete Forever"
        isDestructive={true}
      />
    </div>
  );
}
